import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Server, AlertTriangle, Clock, CheckCircle2, Hexagon } from "lucide-react";
import { validatorsApi } from "../../lib/ledgerApi";

/**
 * Phase 6 polish — super-admin Network Health dashboard.
 * Renders consensus health summary, per-validator 24h participation,
 * byzantine event log, and a "last 50 blocks" table.
 */
export default function AdminNetworkHealth() {
  const [vmetrics, setVmetrics] = useState(null);
  const [cmetrics, setCmetrics] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const [vm, cm] = await Promise.all([
          validatorsApi.adminMetrics(),
          validatorsApi.consensusMetrics(),
        ]);
        if (alive) {
          setVmetrics(vm);
          setCmetrics(cm);
        }
      } catch {
        // ignore transient errors; we'll retry on next tick
      }
    };
    tick();
    const i = setInterval(tick, 5000);
    return () => {
      alive = false;
      clearInterval(i);
    };
  }, []);

  const validators = vmetrics?.validators || [];
  const consensus = vmetrics?.consensus || {};
  const recent = cmetrics?.recent_blocks || [];
  const byzEvents = cmetrics?.byzantine_events_24h || [];
  const perVal24h = cmetrics?.per_validator_24h || [];

  const onlineCount = validators.filter((v) => v.status === "online").length;
  const totalWeight = validators.reduce((a, v) => a + (v.weight || 0), 0);
  const onlineWeight = validators
    .filter((v) => v.status === "online")
    .reduce((a, v) => a + (v.weight || 0), 0);
  const healthy = onlineWeight * 3 >= totalWeight * 2;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="admin-network-health">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">
          / Super-admin · Network health
        </div>
        <h1 className="text-3xl tracking-tight font-medium flex items-center gap-3">
          <Server className="w-7 h-7 text-muted-foreground" strokeWidth={1.5} />
          Validator federation
        </h1>
      </div>

      {/* Top cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm font-mono">
        <Card label="Online" value={`${onlineCount}/${validators.length || "—"}`} testid="nh-card-online" tone={healthy ? "ok" : "bad"} />
        <Card label="Online weight" value={`${onlineWeight}/${totalWeight}`} testid="nh-card-weight" />
        <Card label="Consensus" value={healthy ? "healthy" : "stalled"} testid="nh-card-consensus" tone={healthy ? "ok" : "bad"} />
        <Card label="Committed blocks" value={consensus.committed_blocks ?? "—"} testid="nh-card-committed" />
        <Card label="Stalled rounds" value={consensus.stalled_rounds ?? "—"} testid="nh-card-stalled" tone={consensus.stalled_rounds ? "warn" : ""} />
        <Card label="Byzantine events" value={consensus.byzantine_events ?? "—"} testid="nh-card-byz" tone={consensus.byzantine_events ? "warn" : ""} />
        <Card label="p50 round (ms)" value={consensus.round_duration_ms_p50 ?? "—"} testid="nh-card-p50" />
        <Card label="p95 round (ms)" value={consensus.round_duration_ms_p95 ?? "—"} testid="nh-card-p95" />
      </div>

      {/* Per-validator strip */}
      <div className="border border-border rounded-sm bg-card overflow-hidden mb-6" data-testid="nh-per-validator">
        <div className="px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Per-validator · last 24h participation
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border">
          {validators.map((v) => {
            const last24 = perVal24h.find((p) => p.validator_id === v.id) || {};
            return (
              <div key={v.id} className="p-4" data-testid={`nh-validator-${v.name}`}>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="text-sm font-medium">{v.name}</div>
                  <StatusPill s={v.status} />
                </div>
                <div className="font-mono text-[10px] text-muted-foreground truncate mb-2" title={v.pubkey}>
                  {v.pubkey?.slice(0, 24)}…
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono">
                  <Cell label="Weight" value={v.weight} />
                  <Cell label="Lifetime signed" value={v.blocks_signed_count ?? 0} />
                  <Cell label="Lifetime missed" value={v.blocks_missed_count ?? 0} />
                  <Cell label="24h" value={`${last24.blocks_signed_in_window ?? 0}/${last24.blocks_in_window ?? 0}`} />
                  <Cell label="24h %" value={`${last24.participation_pct_24h ?? "—"}%`} />
                  <Cell label="Last seen" value={v.last_heartbeat_at?.slice(11, 19) || "—"} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Byzantine events log */}
      <div className="border border-border rounded-sm bg-card overflow-hidden mb-6" data-testid="nh-byz-events">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Byzantine events (last 24h)
          </div>
          <div className="text-[10px] font-mono text-muted-foreground">{byzEvents.length}</div>
        </div>
        {byzEvents.length === 0 ? (
          <div className="p-5 text-xs text-muted-foreground inline-flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-status-ok" strokeWidth={1.5} />
            No byzantine events.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {byzEvents.map((b, i) => (
              <div key={i} className="px-5 py-2 grid grid-cols-12 gap-2 text-[11px] font-mono items-center">
                <div className="col-span-2">#{b.block_number}</div>
                <div className="col-span-4 truncate">{b.validator_name}</div>
                <div className="col-span-3 text-status-tampered uppercase tracking-widest text-[9px]">
                  <AlertTriangle className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                  {b.reason}
                </div>
                <div className="col-span-3 text-right text-muted-foreground">
                  {b.timestamp?.slice(11, 19)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Last 50 blocks */}
      <div className="border border-border rounded-sm bg-card overflow-hidden" data-testid="nh-recent-blocks">
        <div className="px-5 py-3 border-b border-border text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          Last {recent.length} blocks
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <Th>Height</Th>
                <Th>Proposer</Th>
                <Th>Signers</Th>
                <Th>Weight</Th>
                <Th>Time</Th>
                <Th>Δms</Th>
                <Th>Type</Th>
              </tr>
            </thead>
            <tbody>
              {recent.map((b) => (
                <tr key={b.block_number} className="border-t border-border hover:bg-muted/30">
                  <Td>
                    <Link to={`/blocks/${b.block_number}`} className="hover:underline" data-testid={`nh-block-${b.block_number}`}>
                      #{b.block_number}
                    </Link>
                  </Td>
                  <Td>{b.proposer_name || (b.legacy_single_sealer ? "—" : "?")}</Td>
                  <Td>
                    <div className="flex gap-1 flex-wrap">
                      {(b.validator_signatures || []).map((s, i) => (
                        <span
                          key={i}
                          title={s.validator_name}
                          className={`px-1 py-0.5 border rounded-sm text-[9px] uppercase tracking-widest ${
                            s.byzantine_flag
                              ? "border-status-tampered/40 bg-status-tampered/10 text-status-tampered"
                              : s.valid
                              ? "border-status-ok/40 bg-status-ok/10 text-status-ok"
                              : "border-muted-foreground/30 bg-muted text-muted-foreground"
                          }`}
                        >
                          {s.validator_name?.replace("validator-", "")}
                        </span>
                      ))}
                    </div>
                  </Td>
                  <Td>
                    {b.legacy_single_sealer
                      ? "—"
                      : `${b.consensus_valid_weight ?? "?"}/${b.consensus_threshold ?? "?"}`}
                  </Td>
                  <Td>{b.timestamp?.slice(11, 19)}</Td>
                  <Td>{b.round_duration_ms ?? "—"}</Td>
                  <Td>
                    {b.legacy_single_sealer ? (
                      <span className="text-amber-600 dark:text-amber-400">legacy</span>
                    ) : b.byzantine_detected ? (
                      <span className="text-status-tampered">byz</span>
                    ) : (
                      <span className="text-status-ok">ok</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, testid, tone }) {
  const t =
    tone === "ok"
      ? "border-status-ok/40 bg-status-ok/5"
      : tone === "bad"
      ? "border-status-tampered/40 bg-status-tampered/5"
      : tone === "warn"
      ? "border-amber-500/40 bg-amber-500/5"
      : "border-border bg-card";
  return (
    <div data-testid={testid} className={`border ${t} rounded-sm px-3 py-2`}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-0.5">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function Cell({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="text-foreground">{value}</div>
    </div>
  );
}

function StatusPill({ s }) {
  const map = {
    online: "border-status-ok/40 bg-status-ok/10 text-status-ok",
    offline: "border-muted-foreground/30 bg-muted text-muted-foreground",
    byzantine: "border-status-tampered/40 bg-status-tampered/10 text-status-tampered",
  };
  return (
    <span className={`px-1.5 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-widest ${map[s] || "border-border"}`}>
      {s}
    </span>
  );
}

function Th({ children }) {
  return <th className="px-3 py-2 text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{children}</th>;
}

function Td({ children }) {
  return <td className="px-3 py-2">{children}</td>;
}
